import * as React from 'react';

import { translate } from '~l10n/client';

import { CreateFolderEventAdapter } from '~analytics/adapters/CreateFolderEventAdapter';
import { UIButton } from '~shared/components/buttons';
import { Dialog, DialogProps } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { useAutoFocus } from '~shared/components/helpers';
import { Inline } from '~shared/components/Inline';
import TextInput from '~shared/components/TextInput';
import {
  QuizToastActions,
  apiToaster,
} from '~teacher/components/quizzes/toastActions';
import { useCreateWorkspaceFolder } from '~teacher/hook/workspaces/useCreateWorkspaceFolder';

/**
 * Clears the folder name when the modal is opened.
 */
function ResetModalForm({
  open,
  setNewFolderName,
  setIsDisabled,
}: {
  open: boolean;
  setNewFolderName: React.Dispatch<React.SetStateAction<string>>;
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  React.useEffect(() => {
    if (open) {
      setNewFolderName(translate('Untitled Folder'));
      setIsDisabled(false);
    }
  }, [open, setNewFolderName, setIsDisabled]);
}

interface Props extends Pick<DialogProps, 'open'> {
  parentId: string;
  onClose: () => void;
  workspaceId: string;
}

export function CreateWorkspaceFolderDialog({
  onClose,
  parentId,
  open,
  workspaceId,
}: Props) {
  const [newFolderName, setNewFolderName] = React.useState<string>();
  const [isDisabled, setIsDisabled] = React.useState<boolean>(false);

  const onFolderNameChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setNewFolderName(event.target.value);

  ResetModalForm({
    open: open,
    setNewFolderName: setNewFolderName,
    setIsDisabled: setIsDisabled,
  });

  const useCreateWorkspaceFolderMtn = useCreateWorkspaceFolder(workspaceId);
  const autoFocusOnOpenCallback = useAutoFocus();

  const createWorkspaceFolder = () => {
    setIsDisabled(true);
    useCreateWorkspaceFolderMtn.mutate(
      {
        workspaceId: workspaceId,
        folderName: newFolderName,
        parentId: parentId,
      },
      {
        onSuccess: () => {
          new CreateFolderEventAdapter({
            workspace_id: workspaceId,
          }).sendAnalytics();
          onClose();
        },
        onError: (error) => {
          setIsDisabled(false);
          apiToaster([], QuizToastActions.CREATE_FOLDER, error);
        },
      }
    );
  };
  return (
    <Dialog
      onClose={onClose}
      id="add-folder-modal"
      open={open}
      title={translate('New Folder')}
      closeLabel={translate('Cancel')}
    >
      <div className="mt-5 mb-5">
        <Divider />
      </div>
      <div className="text-base leading-8">
        <label htmlFor="folder-name">{translate('Folder name')}</label>
        <TextInput
          maxLength={255}
          ref={autoFocusOnOpenCallback}
          id="folder-name"
          defaultValue={newFolderName}
          onChange={onFolderNameChange}
          className="mt-3"
        />
      </div>
      <div className="pt-8">
        <Inline align="right">
          <UIButton onClick={onClose}>{translate('Cancel')}</UIButton>
          <UIButton
            appearance="primary"
            isDisabled={isDisabled}
            onClick={createWorkspaceFolder}
          >
            {translate('Create')}
          </UIButton>
        </Inline>
      </div>
    </Dialog>
  );
}
